// TODO @julianna: Refactor image-upload-hooks for reuse across the funnel and portal to eliminate code duplication (AG-572)
import { arrayMoveImmutable } from "array-move"
import { nanoid } from "nanoid"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  marketingClient,
  useMutation,
  useQueryClient,
  userClient,
} from "@trueskin-web/apis"
import { equalArraysValues } from "@trueskin-web/functions"
import { fileUploadService } from "@trueskin-web/services"

const POSITIONS = ["front", "right", "left"]

const PhotosContext = createContext()

const transformPhotosOrderToPositions = (photosOrder) =>
  Object.keys(
    Object.fromEntries(
      Object.entries(photosOrder).sort(([, a], [, b]) => a - b)
    )
  )

function PhotosProvider({
  photosOrder = { front: 1, right: 2, left: 3 },
  required = true,
  ...props
}) {
  // We always validate object before we do work with it
  if (!Object.values(photosOrder).every((val) => isFinite(val) && val >= 0)) {
    throw new Error(
      "PhotosProvider: photosOrder prop misconfigured! It must have numeric positive finite values"
    )
  }

  if (!equalArraysValues(POSITIONS, Object.keys(photosOrder))) {
    throw new Error(
      "PhotosProvider: photosOrder prop misconfigured! It must have the exact keys: front, right, left!"
    )
  }

  const queryClient = useQueryClient()

  const [uploadError, setUploadError] = useState()

  const positions = transformPhotosOrderToPositions(photosOrder)

  const [photos, setPhotos] = useState(positions.map(() => ({ key: nanoid() })))

  const photosLength = useMemo(() => {
    return photos.reduce((acc, item) => (item.file ? acc + 1 : acc), 0)
  }, [photos])

  const hasRequiredPhotos = required
    ? photos.every((photo) => !!photo.file)
    : true

  const { mutateAsync: uploadImage, error: uploadImageError } = useMutation(
    userClient.uploadImage
  )

  const { mutateAsync: tempUploadImage, error: tempUploadImageError } =
    useMutation(userClient.tempUploadImage)

  const { mutateAsync: skipImageUpload, error: skipImageUploadError } =
    useMutation(userClient.skipImageUpload, {
      onSuccess: () => {
        queryClient.removeQueries("consultations")
      },
    })

  const { mutateAsync: tempSkipImageUpload, error: tempSkipImageUploadError } =
    useMutation(userClient.tempSkipImageUpload, {
      onSuccess: () => {
        queryClient.removeQueries("consultations")
      },
    })

  const { mutateAsync: uploadImages, isLoading: isUploadImagesLoading } =
    useMutation(
      async ({ consultationId, consultationMessageId, tempId, onSuccess }) => {
        if (!hasRequiredPhotos) {
          return
        }

        marketingClient.sendEvent({
          name: marketingClient.EVENT_TYPES_ENUM.UPLOAD_IMAGES_SUBMITTED,
        })

        if (consultationId) {
          for (const [index, { file }] of photos.entries()) {
            if (file) {
              await uploadImage({
                consultationId,
                consultationMessageId,
                file,
                orientation: positions[index],
                totalImagesToUpload: photosLength,
              })
            }
          }
        } else {
          for (const [index, { file }] of photos.entries()) {
            if (file) {
              await tempUploadImage({
                tempId,
                file,
                orientation: positions[index],
              })
            }
          }
        }

        marketingClient.sendEvent({
          name: marketingClient.EVENT_TYPES_ENUM.UPLOAD_IMAGES_SUCCEEDED,
        })

        queryClient.removeQueries("consultations")

        if (onSuccess) {
          await onSuccess({ skip: false, photosLength })
        }
      }
    )

  const { mutateAsync: skipUpload, isLoading: isSkipUploadLoading } =
    useMutation(async ({ consultationId, tempId, onSuccess }) => {
      if (consultationId) {
        await skipImageUpload(consultationId)
      } else {
        await tempSkipImageUpload(tempId)
      }

      if (onSuccess) {
        await onSuccess({ skip: true })
      }
    })

  const handleSortEnd = useCallback(({ oldIndex, newIndex }) => {
    setPhotos((prevPhotos) =>
      arrayMoveImmutable(prevPhotos, oldIndex, newIndex)
    )
  }, [])

  const handleAdd = useCallback(({ index, photo }) => {
    try {
      fileUploadService.validateUploadSize({ file: photo.file })
      marketingClient.sendEvent({
        name: marketingClient.EVENT_TYPES_ENUM.UPLOAD_IMAGES_SELECTED,
      })

      setPhotos((prevPhotos) => {
        const newPhotos = [...prevPhotos]
        newPhotos[index] = photo
        return newPhotos
      })

      setUploadError(null)
    } catch (error) {
      setUploadError(error)
    }
  }, [])

  const handleAddMultiple = useCallback((photos) => {
    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.UPLOAD_IMAGES_SELECTED,
    })

    setPhotos((prevPhotos) => {
      const newPhotos = [...prevPhotos]

      photos.forEach((photo, index) => {
        try {
          fileUploadService.validateUploadSize({ file: photo.file })
          newPhotos[index] = photo
        } catch (error) {
          setUploadError(error)
        }
      })

      return newPhotos
    })
  }, [])

  const handleRemove = useCallback((index) => {
    setPhotos((prevPhotos) => {
      const newPhotos = [...prevPhotos]

      newPhotos[index] = {
        key: nanoid(),
      }

      return newPhotos
    })
    setUploadError(null)
  }, [])

  useEffect(() => setUploadError(uploadImageError), [uploadImageError])
  useEffect(() => setUploadError(tempUploadImageError), [tempUploadImageError])
  useEffect(() => setUploadError(skipImageUploadError), [skipImageUploadError])
  useEffect(
    () => setUploadError(tempSkipImageUploadError),
    [tempSkipImageUploadError]
  )

  const value = useMemo(
    () => ({
      photos,
      photosOrder,
      positions,
      hasRequiredPhotos,
      skipUpload,
      isSkipUploadLoading,
      uploadImages,
      isUploadImagesLoading,
      uploadError,
      handleAdd,
      handleAddMultiple,
      handleRemove,
      handleSortEnd,
    }),
    [
      photos,
      photosOrder,
      positions,
      hasRequiredPhotos,
      skipUpload,
      isSkipUploadLoading,
      uploadImages,
      isUploadImagesLoading,
      uploadError,
      handleAdd,
      handleAddMultiple,
      handleRemove,
      handleSortEnd,
    ]
  )

  return <PhotosContext.Provider value={value} {...props} />
}

function usePhotos() {
  const context = useContext(PhotosContext)
  if (context === undefined) {
    throw new Error(`usePhotos must be used within a PhotosProvider`)
  }
  return context
}

export { PhotosProvider, usePhotos }
